// Public TV install landing page.
//
// Customer-facing, no-login. Lives at:
//   /tv            (default — shows both Fire TV and Google TV cards)
//   /tv/fire-tv    (Fire TV card highlighted)
//   /tv/google-tv  (Google TV card highlighted)
//
// Both cards prefer a Supabase-hosted APK (uploaded by an admin via the
// Downloads page), and fall back to the latest GitHub Release if none has
// been uploaded yet.

import { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  Loader2,
  Tv2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  getFireTvApkAsset,
  getGoogleTvApkAsset,
} from '@/services/releaseStorage';
import {
  resolveFireTvApkDownload,
  type FireTvApkDownload,
} from '@/services/fireTvApk';
import {
  resolveGoogleTvApkDownload,
  type GoogleTvApkDownload,
} from '@/services/googleTvApk';

type TvVariant = 'fire-tv' | 'google-tv';
type AnyApk = FireTvApkDownload | GoogleTvApkDownload;

interface TvInstallPageProps {
  /** When set, only that card is shown. Used by /tv/fire-tv and /tv/google-tv. */
  highlight?: TvVariant;
}

interface PlatformState {
  apk: AnyApk | null;
  loading: boolean;
  error: string | null;
}

const initialState: PlatformState = { apk: null, loading: true, error: null };

export function TvInstallPage({ highlight }: TvInstallPageProps = {}) {
  const [fireTv, setFireTv] = useState<PlatformState>(initialState);
  const [googleTv, setGoogleTv] = useState<PlatformState>(initialState);
  const [copied, setCopied] = useState<string | null>(null);

  const shortUrl = useMemo(
    () => (typeof window !== 'undefined' ? `${window.location.origin}/tv` : '/tv'),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    async function loadFireTv() {
      setFireTv({ apk: null, loading: true, error: null });
      try {
        const fromStorage = await getFireTvApkAsset();
        if (cancelled) return;
        if (fromStorage) {
          setFireTv({ apk: fromStorage, loading: false, error: null });
          return;
        }
        const fromGithub = await resolveFireTvApkDownload();
        if (cancelled) return;
        setFireTv({ apk: fromGithub, loading: false, error: null });
      } catch {
        if (cancelled) return;
        setFireTv({
          apk: null,
          loading: false,
          error: 'No Fire TV APK is currently published. Ask your admin to upload one.',
        });
      }
    }
    async function loadGoogleTv() {
      setGoogleTv({ apk: null, loading: true, error: null });
      try {
        const fromStorage = await getGoogleTvApkAsset();
        if (cancelled) return;
        if (fromStorage) {
          setGoogleTv({ apk: fromStorage, loading: false, error: null });
          return;
        }
        const fromGithub = await resolveGoogleTvApkDownload();
        if (cancelled) return;
        setGoogleTv({ apk: fromGithub, loading: false, error: null });
      } catch {
        if (cancelled) return;
        setGoogleTv({
          apk: null,
          loading: false,
          error:
            'No Google TV APK is currently published. Ask your admin to upload one.',
        });
      }
    }
    void loadFireTv();
    void loadGoogleTv();
    return () => {
      cancelled = true;
    };
  }, []);

  async function copy(text: string, key: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {
      setCopied(null);
    }
  }

  const showFireTv = highlight !== 'google-tv';
  const showGoogleTv = highlight !== 'fire-tv';

  return (
    <main className="min-h-screen bg-background px-4 py-10 text-foreground">
      <div className="mx-auto w-full max-w-4xl space-y-6">
        <header className="space-y-2 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs text-muted-foreground">
            <Tv2 className="h-3.5 w-3.5" /> TV Player Setup
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Install Active Sign Player
          </h1>
          <p className="text-sm text-muted-foreground">
            Pick your TV platform below. Once installed, the player shows a
            6-character pairing code that an admin claims from the dashboard.
          </p>
        </header>

        <section className="space-y-2 rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Short URL
          </div>
          <div className="flex gap-2">
            <Input value={shortUrl} readOnly className="font-mono text-sm" />
            <Button
              variant="outline"
              onClick={() => void copy(shortUrl, 'short')}
            >
              <Copy className="mr-1 h-4 w-4" />
              {copied === 'short' ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Open this URL on your TV (in Downloader / TV Bro / your browser of
            choice) to land back here.
          </p>
        </section>

        <div className="grid gap-4 md:grid-cols-2">
          {showFireTv && (
            <FireTvCard
              state={fireTv}
              copied={copied}
              onCopy={(value, key) => void copy(value, key)}
            />
          )}
          {showGoogleTv && (
            <GoogleTvCard
              state={googleTv}
              copied={copied}
              onCopy={(value, key) => void copy(value, key)}
            />
          )}
        </div>

        <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5" />
          After install, the player opens provisioning and shows a pairing
          code.
        </p>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

interface CardProps {
  state: PlatformState;
  copied: string | null;
  onCopy: (value: string, key: string) => void;
}

function FireTvCard({ state, copied, onCopy }: CardProps) {
  return (
    <section className="space-y-4 rounded-lg border p-5">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">Fire TV / Fire Stick</h2>
        <p className="text-sm text-muted-foreground">
          Install via the Fire TV <strong>Downloader</strong> app. No Amazon
          Appstore submission, no ADB required.
        </p>
      </div>

      <ol className="list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
        <li>Install the <strong>Downloader</strong> app from the Amazon Appstore.</li>
        <li>
          Settings → My Fire TV → Developer options: enable
          <em> Apps from Unknown Sources</em> for Downloader.
        </li>
        <li>Open Downloader, type the short URL above, hit Go.</li>
        <li>Tap <strong>Download APK</strong>, then <strong>Install</strong>.</li>
        <li>Open <strong>Active Sign Player</strong> and pair the code.</li>
      </ol>

      <ApkDetails
        state={state}
        copyKeyPrefix="firetv"
        copied={copied}
        onCopy={onCopy}
        guideTitle="Full Fire TV guide"
      />
    </section>
  );
}

function GoogleTvCard({ state, copied, onCopy }: CardProps) {
  return (
    <section className="space-y-4 rounded-lg border p-5">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">Google TV / Android TV</h2>
        <p className="text-sm text-muted-foreground">
          Install via any browser, downloader, or file manager on the device.
          <strong> No Google Play publishing required. No ADB required for
          normal installs.</strong>
        </p>
      </div>

      <ol className="list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
        <li>
          From Google Play on your TV, install <strong>Downloader</strong>,
          <strong> TV Bro</strong>, or any file manager that can open URLs.
        </li>
        <li>Open the short URL above in that app and pick this card.</li>
        <li>Tap <strong>Download APK</strong>.</li>
        <li>
          When prompted, allow <em>Install unknown apps</em> for the
          downloader/browser app you used.
        </li>
        <li>Approve the APK install.</li>
        <li>Open <strong>Active Sign Player</strong> and pair the code.</li>
      </ol>

      <ApkDetails
        state={state}
        copyKeyPrefix="googletv"
        copied={copied}
        onCopy={onCopy}
        guideTitle="Full Google TV guide"
      />
    </section>
  );
}

interface ApkDetailsProps {
  state: PlatformState;
  copyKeyPrefix: string;
  copied: string | null;
  onCopy: (value: string, key: string) => void;
  guideTitle: string;
}

function ApkDetails({
  state,
  copyKeyPrefix,
  copied,
  onCopy,
  guideTitle,
}: ApkDetailsProps) {
  if (state.loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking latest APK…
      </div>
    );
  }
  if (state.error || !state.apk) {
    return (
      <div className="rounded border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
        {state.error ?? 'No APK is currently available.'}
      </div>
    );
  }

  const apk = state.apk;
  const apkCopyKey = `${copyKeyPrefix}-apk`;

  return (
    <div className="space-y-3">
      <div className="rounded-md bg-muted/40 p-3 text-sm">
        <div className="flex items-center justify-between gap-3">
          <span className="font-mono text-xs">{apk.fileName}</span>
          <span className="text-xs text-muted-foreground">
            {apk.version ?? 'unknown'}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button asChild>
          <a href={apk.downloadUrl} target="_blank" rel="noopener noreferrer">
            <Download className="mr-1 h-4 w-4" /> Download APK
          </a>
        </Button>
        <Button
          variant="outline"
          onClick={() => onCopy(apk.downloadUrl, apkCopyKey)}
        >
          <Copy className="mr-1 h-4 w-4" />
          {copied === apkCopyKey ? 'Copied' : 'Copy APK URL'}
        </Button>
        <Button variant="ghost" asChild>
          <a
            href={apk.sideloadGuideUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {guideTitle} <ExternalLink className="ml-1 h-4 w-4" />
          </a>
        </Button>
      </div>
      {apk.sha256 && (
        <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
          <div className="mb-1 font-medium text-foreground">
            Verify APK (optional)
          </div>
          <div className="break-all font-mono">{apk.sha256}</div>
        </div>
      )}
    </div>
  );
}
